import React, { useState, useRef, useEffect, useCallback } from 'react';
import useTranslation from '../../hooks/useTranslation';
import {
  convertToWav,
  predictWithWavBlob,
  corporateCreateSession,
  corporateSaveEmotionDetails
} from '../api';
import { EMOTION_KEYS } from '../../utils/emotionConstants';

function parseEmotionsFromTop5(top5String) {
  const emotions = {};
  const regex = /([a-z]+)\s+(\d+(?:\.\d+)?)/gi;
  let match;
  while ((match = regex.exec(top5String)) !== null) {
    emotions[match[1].toLowerCase()] = parseFloat(match[2]);
  }
  return emotions;
}

function addEmotionBasedVariation(baseMetrics, emotionComposition) {
  if (emotionComposition.angry > 35 && emotionComposition.angry > emotionComposition.sad) {
    return { ...baseMetrics, stress_score: Math.min(100, baseMetrics.stress_score + 15), engagement_score: Math.max(0, baseMetrics.engagement_score - 12) };
  }
  if (emotionComposition.sad > 35 && emotionComposition.sad > emotionComposition.angry) {
    return { ...baseMetrics, motivation_score: Math.max(0, baseMetrics.motivation_score - 20), burnout_score: Math.min(100, baseMetrics.burnout_score + 8) };
  }
  if (emotionComposition.fear > 35) {
    return { ...baseMetrics, stress_score: Math.min(100, baseMetrics.stress_score + 20), engagement_score: Math.max(0, baseMetrics.engagement_score - 10) };
  }
  return baseMetrics;
}

function mapPredictionToMetricsSchoolStyle(apiResult) {
  if (!apiResult || apiResult._noVoice) {
    return { emotion_score: null, motivation_score: null, burnout_score: null, engagement_score: null, stress_score: null, wellbeing_score: null, isNegativeOnlyClip: false, positiveShare: 0, negativeShare: 0 };
  }

  const res = (apiResult && (apiResult.RES || apiResult.res || apiResult.data || apiResult)) || {};
  const top5String = res.top_5 || apiResult.top_5 || '';

  if (!top5String || typeof top5String !== 'string') {
    return { emotion_score: null, motivation_score: null, burnout_score: null, engagement_score: null, stress_score: null, wellbeing_score: null, isNegativeOnlyClip: false, positiveShare: 0, negativeShare: 0 };
  }

  const scores = {};
  EMOTION_KEYS.forEach(em => {
    try {
      const re = new RegExp(`${em}\\s*[:=]?\\s*(\\d+(?:\\.\\d+)?)%?`, 'i');
      const m = top5String.match(re);
      scores[em] = m ? parseFloat(m[1]) / 100 : 0;
    } catch { scores[em] = 0; }
  });

  const calm = scores.happy ?? 0;
  const stressed = scores.angry ?? 0;
  const demotivated = scores.sad ?? 0;
  const anxious = scores.fear ?? 0;
  const hurt = scores.disgust ?? 0;

  const calmPct = calm * 100;
  const stressedPct = stressed * 100;
  const demotivatedPct = demotivated * 100;
  const anxiousPct = anxious * 100;
  const hurtPct = hurt * 100;

  const positive = calmPct;
  const negative = stressedPct + demotivatedPct + anxiousPct + hurtPct;
  const total = positive + negative || 1;
  const positiveShare = positive / total;
  const negativeShare = negative / total;
  const isNegativeOnly = positiveShare < 0.05 && negativeShare > 0.5;

  const hasNoEmotionData = calmPct === 0 && stressedPct === 0 && demotivatedPct === 0 && anxiousPct === 0 && hurtPct === 0;
  if (hasNoEmotionData) {
    return { emotion_score: null, motivation_score: null, burnout_score: null, engagement_score: null, stress_score: null, wellbeing_score: null, isNegativeOnlyClip: false, positiveShare: 0, negativeShare: 0 };
  }

  const positiveEmotionPct = calmPct;
  const negativeEmotionPct = stressedPct + demotivatedPct + anxiousPct + hurtPct;

  const emotionScoreRaw = 50 + (positiveEmotionPct * 0.5) - (negativeEmotionPct * 0.3);
  const emotion = Math.max(0, Math.min(100, emotionScoreRaw));

  const motivationRaw = 50 + (positiveEmotionPct * 0.4) - (demotivatedPct * 0.5) - (stressedPct * 0.3);
  const motivation = Math.max(0, Math.min(100, motivationRaw));

  const stressedContrib = stressedPct * 0.4;
  const anxiousContrib = anxiousPct * 0.3;
  const hurtContrib = hurtPct * 0.15;
  const demotivatedContrib = demotivatedPct * 0.15;
  const burnout = Math.max(0, Math.min(100, stressedContrib + anxiousContrib + hurtContrib + demotivatedContrib));

  const engagementScore = (calmPct * 0.5);
  const resilienceScore = Math.max(0, 100 - ((stressedPct + anxiousPct + demotivatedPct) / 3));
  const engagement = Math.max(0, Math.min(100, (engagementScore * 0.6) + (resilienceScore * 0.4)));

  const stressFromAnxious = anxiousPct * 1.0;
  const stressFromStressed = stressedPct * 0.8;
  const stress = Math.max(0, Math.min(100, stressFromAnxious + stressFromStressed));

  const wellbeingFromCalm = calmPct * 0.5;
  const wellbeingFromLowStress = (100 - stress) * 0.3;
  const wellbeingFromLowBurnout = (100 - burnout) * 0.2;
  const wellbeing = Math.max(0, Math.min(100, 50 + wellbeingFromCalm + wellbeingFromLowStress + wellbeingFromLowBurnout));

  let finalEmotion = Math.round(emotion);
  let finalMotivation = Math.round(motivation);
  let finalBurnout = Math.round(burnout);
  let finalEngagement = Math.round(engagement);
  let finalStress = Math.round(stress);
  let finalWellbeing = Math.round(wellbeing);

  const emotionComposition = parseEmotionsFromTop5(top5String);
  const baseMetrics = { emotion_score: finalEmotion, motivation_score: finalMotivation, burnout_score: finalBurnout, engagement_score: finalEngagement, stress_score: finalStress, wellbeing_score: finalWellbeing };
  const variatedMetrics = addEmotionBasedVariation(baseMetrics, emotionComposition);

  return {
    emotion_score: variatedMetrics.emotion_score,
    motivation_score: variatedMetrics.motivation_score,
    burnout_score: variatedMetrics.burnout_score,
    engagement_score: variatedMetrics.engagement_score,
    stress_score: variatedMetrics.stress_score,
    wellbeing_score: variatedMetrics.wellbeing_score,
    isNegativeOnlyClip: isNegativeOnly,
    positiveShare,
    negativeShare
  };
}

function mapApiResultToMetrics(apiResult) {
  return mapPredictionToMetricsSchoolStyle(apiResult);
}

function sanitizeErrorMessage(msg) {
  if (!msg || typeof msg !== 'string') return 'Something went wrong. Please try again.';
  const lower = msg.toLowerCase();
  if (/createbuffer|decodeaudiodata|domexception|failed to execute|parsing|decode|buffer|audiocontext|not enough arguments/i.test(lower)) {
    return 'Something went wrong while processing the recording. Please try again.';
  }
  return msg;
}

function formatTimer(seconds) {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
}

function audioBufferToWav(audioBuffer) {
  const numChannels = audioBuffer.numberOfChannels;
  const sampleRate = audioBuffer.sampleRate;
  const length = audioBuffer.length;
  const bytesPerSample = 2;
  const blockAlign = numChannels * bytesPerSample;
  const byteRate = sampleRate * blockAlign;
  const dataSize = length * blockAlign;
  const buffer = new ArrayBuffer(44 + dataSize);
  const view = new DataView(buffer);

  const writeString = (offset, string) => {
    for (let i = 0; i < string.length; i++) {
      view.setUint8(offset + i, string.charCodeAt(i));
    }
  };

  writeString(0, 'RIFF');
  view.setUint32(4, 36 + dataSize, true);
  writeString(8, 'WAVE');
  writeString(12, 'fmt ');
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, numChannels, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, byteRate, true);
  view.setUint16(32, blockAlign, true);
  view.setUint16(34, 16, true);
  writeString(36, 'data');
  view.setUint32(40, dataSize, true);

  let offset = 44;
  for (let i = 0; i < length; i++) {
    for (let channel = 0; channel < numChannels; channel++) {
      const sample = Math.max(-1, Math.min(1, audioBuffer.getChannelData(channel)[i]));
      view.setInt16(offset, sample < 0 ? sample * 0x8000 : sample * 0x7FFF, true);
      offset += 2;
    }
  }

  return new Blob([buffer], { type: 'audio/wav' });
}

async function splitAudioBlob(audioBlob, totalDurationSeconds = 40, numChunks = 5) {
  const chunkDurationSeconds = totalDurationSeconds / numChunks;
  const audioContext = new (window.AudioContext || window.webkitAudioContext)();
  const arrayBuffer = await audioBlob.arrayBuffer();
  const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
  const sampleRate = audioBuffer.sampleRate;
  const channels = audioBuffer.numberOfChannels;
  const totalSamples = audioBuffer.length;
  const samplesPerChunk = Math.floor(sampleRate * chunkDurationSeconds);
  const chunks = [];

  for (let i = 0; i < numChunks; i++) {
    const startSample = i * samplesPerChunk;
    const endSample = Math.min(startSample + samplesPerChunk, totalSamples);
    const chunkSamples = endSample - startSample;
    const chunkBuffer = audioContext.createBuffer(channels, chunkSamples, sampleRate);
    for (let channel = 0; channel < channels; channel++) {
      const sourceData = audioBuffer.getChannelData(channel);
      const chunkData = chunkBuffer.getChannelData(channel);
      for (let j = 0; j < chunkSamples; j++) {
        chunkData[j] = sourceData[startSample + j];
      }
    }
    const wavBlob = audioBufferToWav(chunkBuffer);
    chunks.push(wavBlob);
  }

  audioContext.close();
  return chunks;
}

async function analyzeAudioForVoice(audioBlob) {
  return new Promise((resolve) => {
    try {
      if (!audioBlob || audioBlob.size < 1000) {
        resolve({ hasVoice: false, reason: 'file_too_small' });
        return;
      }
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const fileReader = new FileReader();
      const timeout = setTimeout(() => { resolve({ hasVoice: false, reason: 'analysis_timeout' }); }, 3000);

      fileReader.onload = async (e) => {
        try {
          clearTimeout(timeout);
          const arrayBuffer = e.target.result;
          if (!arrayBuffer || arrayBuffer.byteLength === 0) { resolve({ hasVoice: false, reason: 'empty_buffer' }); return; }
          const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
          const audioData = audioBuffer.getChannelData(0);
          let sum = 0, maxAbs = 0, energyVariance = 0, zeroCrossings = 0;
          const sampleStep = Math.max(1, Math.floor(audioData.length / 2000));
          const samples = [];
          let voicedFrames = 0;

          for (let i = 0; i < audioData.length; i += sampleStep) {
            const abs = Math.abs(audioData[i]);
            sum += abs;
            if (abs > maxAbs) maxAbs = abs;
            samples.push(abs);
            if (abs > 0.01) voicedFrames++;
            if (i > 0 && ((audioData[i] >= 0) !== (audioData[i - sampleStep] >= 0))) zeroCrossings++;
          }

          const avgAmplitude = samples.length ? sum / samples.length : 0;
          for (let i = 0; i < samples.length; i++) energyVariance += Math.pow(samples[i] - avgAmplitude, 2);
          energyVariance = energyVariance / samples.length;
          const zcr = zeroCrossings / samples.length;
          const speechFraction = voicedFrames / samples.length;

          const sorted = [...samples].sort((a, b) => a - b);
          const p10 = sorted[Math.floor(sorted.length * 0.1)] || 0;
          const p90 = sorted[Math.floor(sorted.length * 0.9)] || 0;
          const dynamicRange = p90 - p10;

          let longestRun = 0, currentRun = 0;
          for (let i = 0; i < samples.length; i++) {
            if (samples[i] > 0.01) { currentRun++; longestRun = Math.max(longestRun, currentRun); } else { currentRun = 0; }
          }
          const longestRunSec = (longestRun * sampleStep) / audioBuffer.sampleRate;

          const hasVariation = energyVariance > 0.00005;
          const hasSpeechZCR = zcr > 0.005 && zcr < 0.8;
          const hasReasonableAmplitude = avgAmplitude > 0.008 && avgAmplitude < 0.6;
          const hasReasonablePeak = maxAbs > 0.05 && maxAbs < 1.0;
          const hasEnoughVoiced = speechFraction >= 0.30;
          const hasDynamicRange = dynamicRange >= 0.07;
          const hasContinuousSpeech = longestRunSec >= 0.30;

          const strongSpeech = hasEnoughVoiced && hasDynamicRange && hasContinuousSpeech && hasReasonablePeak && hasSpeechZCR && hasVariation && hasReasonableAmplitude;
          const hasSignificantAmplitude = avgAmplitude > 0.03 || maxAbs > 0.12;
          const hasVoice = strongSpeech || hasSignificantAmplitude;

          resolve({ hasVoice, reason: hasVoice ? 'voice_detected' : 'no_human_voice_detected' });
        } catch (err) {
          resolve({ hasVoice: false, reason: 'analysis_error', error: err?.message });
        }
      };
      fileReader.onerror = () => { clearTimeout(timeout); resolve({ hasVoice: false, reason: 'file_read_error' }); };
      fileReader.readAsArrayBuffer(audioBlob);
    } catch (err) {
      resolve({ hasVoice: false, reason: 'setup_error', error: err?.message });
    }
  });
}

export default function SessionConductModal({
  departments,
  employees,
  onClose,
  onComplete,
  t,
  userId,
  preSelectedDepartment,
  preSelectedStaff
}) {
  const [sessionStep, setSessionStep] = useState(preSelectedStaff ? 3 : 1);
  const [selectedSessionDepartment, setSelectedSessionDepartment] = useState(preSelectedDepartment || null);
  const [selectedSessionStaff, setSelectedSessionStaff] = useState(preSelectedStaff || null);
  const [departmentSearchQuery, setDepartmentSearchQuery] = useState('');
  const [staffSearchQuery, setStaffSearchQuery] = useState('');
  const [sessionRecordings, setSessionRecordings] = useState([null, null, null, null, null]);
  const [sessionResults, setSessionResults] = useState([null, null, null, null, null]);
  const [currentRecordingIndex, setCurrentRecordingIndex] = useState(0);
  const [processingIndex, setProcessingIndex] = useState(-1);
  const [sessionProcessing, setSessionProcessing] = useState(false);
  const [isContinuousMode, setIsContinuousMode] = useState(true);
  const [isPaused, setIsPaused] = useState(false);
  const [recordingState, setRecordingState] = useState({
    isRecording: false,
    timer: 0,
    waveformData: new Uint8Array(64),
    chunkProgress: 0,
    micPermission: false,
    audioChunks: [],
    error: null
  });

  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const streamRef = useRef(null);
  const recordingTimerRef = useRef(null);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const waveformIntervalRef = useRef(null);
  const autoStopTimeoutRef = useRef(null);
  const isPausedRef = useRef(false);
  const recordingWasAutoStartedRef = useRef(false);

  useEffect(() => {
    return () => {
      if (recordingTimerRef.current) clearInterval(recordingTimerRef.current);
      if (waveformIntervalRef.current) clearInterval(waveformIntervalRef.current);
      if (autoStopTimeoutRef.current) clearTimeout(autoStopTimeoutRef.current);
      if (audioContextRef.current) audioContextRef.current.close();
      if (streamRef.current) streamRef.current.getTracks().forEach(track => track.stop());
    };
  }, []);

  useEffect(() => {
    if (recordingTimerRef.current) { clearInterval(recordingTimerRef.current); recordingTimerRef.current = null; }
    if (recordingState.isRecording && !isPaused) {
      recordingTimerRef.current = setInterval(() => {
        setRecordingState(prev => {
          if (prev.isRecording && !isPaused) {
            const newTimer = prev.timer + 1;
            const targetDuration = isContinuousMode ? 40 : 8;
            if (newTimer >= targetDuration) {
              if (recordingTimerRef.current) { clearInterval(recordingTimerRef.current); recordingTimerRef.current = null; }
              return { ...prev, timer: targetDuration };
            }
            return { ...prev, timer: newTimer };
          }
          return prev;
        });
      }, 1000);
    }
    return () => { if (recordingTimerRef.current) { clearInterval(recordingTimerRef.current); recordingTimerRef.current = null; } };
  }, [recordingState.isRecording, isPaused, isContinuousMode]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!recordingState.isRecording || sessionStep !== 3) return;
      if (e.code === 'Space' && !e.target.matches('input, textarea, select')) { e.preventDefault(); togglePauseRecording(); }
      if (e.code === 'Escape' && recordingState.isRecording) { e.preventDefault(); stopRecording(); }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [recordingState.isRecording, sessionStep]);

  const getRecordingStatus = useCallback((index) => {
    const rec = sessionRecordings[index];
    const res = sessionResults[index];
    if (!rec) return 'empty';
    if (res?._noVoice || res?._error) return 'invalid';
    if (res && ((typeof res.top_5 === 'string' && res.top_5.length > 0) || (typeof res.top_5 === 'object' && res.top_5 !== null) || (res.emotion && res.motivation))) return 'done';
    return 'pending';
  }, [sessionRecordings, sessionResults]);

  const findNextEmptySlot = useCallback(() => {
    for (let i = 0; i < 5; i++) { if (getRecordingStatus(i) === 'empty') return i; }
    return -1;
  }, [getRecordingStatus]);

  const findFirstSlotToRecord = useCallback(() => {
    for (let i = 0; i < 5; i++) {
      const status = getRecordingStatus(i);
      if (status === 'empty' || status === 'invalid') return i;
    }
    return -1;
  }, [getRecordingStatus]);

  const getCompletedCount = useCallback(() => {
    if (isContinuousMode) return sessionRecordings[0] && getRecordingStatus(0) === 'done' ? 1 : 0;
    return [0, 1, 2, 3, 4].filter(i => getRecordingStatus(i) === 'done').length;
  }, [isContinuousMode, sessionRecordings, getRecordingStatus]);

  const startWaveformVisualization = useCallback((stream) => {
    try {
      if (audioContextRef.current) { try { audioContextRef.current.close(); } catch (e) {} }
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 256;
      analyser.smoothingTimeConstant = 0.3;
      const source = audioContext.createMediaStreamSource(stream);
      source.connect(analyser);
      audioContextRef.current = audioContext;
      analyserRef.current = analyser;
      const bufferLength = analyser.frequencyBinCount;
      const waveformDataArray = new Uint8Array(bufferLength);
      if (waveformIntervalRef.current) { clearInterval(waveformIntervalRef.current); waveformIntervalRef.current = null; }
      waveformIntervalRef.current = setInterval(() => {
        if (!analyserRef.current || !streamRef.current?.active) return;
        try {
          analyserRef.current.getByteFrequencyData(waveformDataArray);
          setRecordingState(prev => {
            if (prev.isRecording && !isPausedRef.current) return { ...prev, waveformData: new Uint8Array(waveformDataArray) };
            return prev;
          });
        } catch (err) {}
      }, 16);
    } catch (err) {
      setRecordingState(prev => ({ ...prev, error: sanitizeErrorMessage("Waveform unavailable: " + (err.message || '')) }));
    }
  }, []);

  const stopWaveformVisualization = useCallback(() => {
    if (waveformIntervalRef.current) { clearInterval(waveformIntervalRef.current); waveformIntervalRef.current = null; }
    if (analyserRef.current) analyserRef.current = null;
    if (audioContextRef.current) { try { audioContextRef.current.close(); } catch (e) {} audioContextRef.current = null; }
    setRecordingState(prev => ({ ...prev, waveformData: new Uint8Array(64) }));
  }, []);

  const stopTimer = useCallback(() => {
    if (recordingTimerRef.current) { clearInterval(recordingTimerRef.current); recordingTimerRef.current = null; }
  }, []);

  const stopRecordingFn = useCallback(() => {
    if (autoStopTimeoutRef.current) { clearTimeout(autoStopTimeoutRef.current); autoStopTimeoutRef.current = null; }
    if (mediaRecorderRef.current) {
      if (mediaRecorderRef.current.state === 'recording') {
        mediaRecorderRef.current.stop();
      } else if (mediaRecorderRef.current.state === 'paused') {
        try {
          mediaRecorderRef.current.resume();
          setTimeout(() => { if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') mediaRecorderRef.current.stop(); }, 100);
        } catch (e) {}
      }
    }
    stopTimer();
    stopWaveformVisualization();
  }, [stopTimer, stopWaveformVisualization]);

  const processSingleRecording = useCallback(async (blob, index, retries = 3) => {
    if (getRecordingStatus(index) === 'done') return;
    for (let attempt = 1; attempt <= retries; attempt++) {
      try {
        setProcessingIndex(index);
        const voiceAnalysis = await analyzeAudioForVoice(blob);
        if (!voiceAnalysis.hasVoice) {
          const reason = voiceAnalysis.reason || 'no_human_voice_detected';
          setSessionResults(prev => {
            const results = [...prev]; while (results.length < 5) results.push(null);
            if (getRecordingStatus(index) !== 'done') results[index] = { _noVoice: true, _reason: reason, _segment: index + 1 };
            return results;
          });
          throw new Error(`No human voice detected in segment ${index + 1}/5: ${reason}`);
        }
        const convRes = await convertToWav(blob);
        const wavBase64 = convRes?.data?.wav_base64;
        if (!wavBase64) throw new Error('Conversion failed: empty WAV data');
        const wavBytes = atob(wavBase64);
        const buf = new Uint8Array(wavBytes.length);
        for (let i = 0; i < wavBytes.length; i++) buf[i] = wavBytes.charCodeAt(i);
        const wavBlob = new Blob([buf], { type: 'audio/wav' });
        const predRes = await predictWithWavBlob(wavBlob);
        const apiRes = predRes?.data;
        const apiResult = apiRes?.RES || apiRes?.result || apiRes?.data || apiRes;
        const hasTop5 = apiResult && ((typeof apiResult.top_5 === 'string' && apiResult.top_5.length > 0) || (typeof apiResult.top_5 === 'object' && apiResult.top_5 !== null) || (apiResult.emotion && apiResult.motivation));
        if (!hasTop5) throw new Error('No emotion data detected in API response');
        setSessionResults(prev => {
          const results = [...prev]; while (results.length < 5) results.push(null);
          if (getRecordingStatus(index) !== 'done') results[index] = apiResult;
          return results;
        });
        setProcessingIndex(-1);
        return;
      } catch (err) {
        if (attempt === retries) { setProcessingIndex(-1); throw err; }
        await new Promise(resolve => setTimeout(resolve, 1000 * attempt));
      }
    }
  }, [getRecordingStatus]);

  const startRecording = useCallback(async (index = null) => {
    if (recordingState.isRecording || sessionProcessing) return;
    try {
      setRecordingState(prev => ({ ...prev, error: null }));
      const stream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true } });
      streamRef.current = stream;
      let recordingIndex;
      if (isContinuousMode) {
        recordingIndex = 0;
        if (getRecordingStatus(0) === 'done') { alert('Recording already completed. Please discard to record again.'); stream.getTracks().forEach(track => track.stop()); return; }
      } else {
        recordingIndex = index !== null ? index : findNextEmptySlot();
        if (recordingIndex === -1) { alert('All 5 recordings are complete'); stream.getTracks().forEach(track => track.stop()); return; }
        if (getRecordingStatus(recordingIndex) === 'done') { stream.getTracks().forEach(track => track.stop()); return; }
      }
      setCurrentRecordingIndex(recordingIndex);
      setIsPaused(false); isPausedRef.current = false;
      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus') ? 'audio/webm;codecs=opus' : 'audio/webm';
      const mediaRecorder = new MediaRecorder(stream, { mimeType });
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];
      mediaRecorder.ondataavailable = (e) => { if (e.data && e.data.size > 0) audioChunksRef.current.push(e.data); };
      mediaRecorder.onerror = (e) => { setRecordingState(prev => ({ ...prev, error: sanitizeErrorMessage(e.error?.message || "Recording error") })); };

      const recordingIndexFinal = recordingIndex;
      let chunkMonitorInterval = null;

      mediaRecorder.onstop = async () => {
        if (chunkMonitorInterval) { clearInterval(chunkMonitorInterval); chunkMonitorInterval = null; }
        const blob = new Blob(audioChunksRef.current, { type: mimeType });
        if (streamRef.current) { streamRef.current.getTracks().forEach(track => track.stop()); streamRef.current = null; }
        stopTimer(); stopWaveformVisualization();
        setRecordingState(prev => ({ ...prev, isRecording: false, chunkProgress: 0, audioChunks: [] }));

        try {
          if (isContinuousMode) {
            setRecordingState(prev => ({ ...prev, chunkProgress: 0 }));
            const audioChunks = await splitAudioBlob(blob, 40, 5);
            setSessionRecordings(prev => { const recordings = [...prev]; while (recordings.length < 5) recordings.push(null); for (let i = 0; i < audioChunks.length; i++) recordings[i] = audioChunks[i]; return recordings; });
            for (let i = 0; i < audioChunks.length; i++) {
              setRecordingState(prev => ({ ...prev, chunkProgress: i + 1 }));
              try { await processSingleRecording(audioChunks[i], i); } catch (err) {
                const userMessage = sanitizeErrorMessage(err.message || 'processing_failed');
                setSessionResults(prev => { const results = [...prev]; while (results.length < 5) results.push(null); results[i] = { _error: true, _reason: err.message || 'processing_failed', _segment: i + 1 }; return results; });
                alert(`Segment ${i + 1}/5 failed: ${userMessage}\n\nYou can re-record this segment by clicking "Start Recording" again.`);
              }
            }
            setRecordingState(prev => ({ ...prev, chunkProgress: 5 }));
          } else {
            const currentStatus = getRecordingStatus(recordingIndexFinal);
            if (currentStatus === 'done') return;
            setSessionRecordings(prev => { const recordings = [...prev]; while (recordings.length < 5) recordings.push(null); if (getRecordingStatus(recordingIndexFinal) !== 'done') recordings[recordingIndexFinal] = blob; return recordings; });
            try {
              await processSingleRecording(blob, recordingIndexFinal);
              if (!recordingWasAutoStartedRef.current) {
              } else {
                recordingWasAutoStartedRef.current = false;
                const nextIndex = findNextEmptySlot();
                if (nextIndex !== -1) {
                  setCurrentRecordingIndex(nextIndex);
                  setTimeout(() => {
                    setRecordingState(prev => {
                      if (!prev.isRecording && !sessionProcessing && processingIndex === -1) { recordingWasAutoStartedRef.current = true; setTimeout(() => startRecording(nextIndex), 100); }
                      return prev;
                    });
                  }, 1500);
                }
              }
            } catch (err) {
              recordingWasAutoStartedRef.current = false;
              const userMessage = sanitizeErrorMessage(err.message || 'processing_failed');
              setSessionResults(prev => { const results = [...prev]; while (results.length < 5) results.push(null); results[recordingIndexFinal] = { _error: true, _reason: err.message || 'processing_failed', _segment: recordingIndexFinal + 1 }; return results; });
              alert(`Segment ${recordingIndexFinal + 1}/5 failed: ${userMessage}\n\nYou can re-record this segment by clicking "Start Recording" again.`);
            }
          }
        } catch (err) {
          const userMessage = sanitizeErrorMessage(err.message || 'Unknown error');
          setRecordingState(prev => ({ ...prev, error: userMessage }));
          alert(`Recording processing failed: ${userMessage}`);
        }
      };

      mediaRecorder.start(1000);
      let chunkMonitorInterval2 = setInterval(() => {
        const chunkCount = audioChunksRef.current.length;
        const recorderState = mediaRecorderRef.current?.state;
        if (recorderState === 'recording' && chunkCount === 0) {}
      }, 2000);
      chunkMonitorInterval = chunkMonitorInterval2;

      setRecordingState(prev => ({ ...prev, isRecording: true, timer: 0, micPermission: true, audioChunks: [], waveformData: new Uint8Array(64) }));
      startWaveformVisualization(stream);

      if (isContinuousMode) {
        autoStopTimeoutRef.current = setTimeout(() => { if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') mediaRecorderRef.current.stop(); }, 40000);
      } else {
        autoStopTimeoutRef.current = setTimeout(() => { if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') mediaRecorderRef.current.stop(); }, 8000);
      }
    } catch (err) {
      const isPermission = /permission|denied|not allowed|microphone/i.test((err.message || '').toLowerCase());
      const userError = isPermission ? "Microphone access denied" : sanitizeErrorMessage(err.message || "Recording failed");
      setRecordingState(prev => ({ ...prev, error: userError, isRecording: false }));
      alert(isPermission ? "Microphone access needed. Please allow microphone permission." : userError);
    }
  }, [recordingState.isRecording, sessionProcessing, isContinuousMode, getRecordingStatus, findNextEmptySlot, processSingleRecording, startWaveformVisualization, stopTimer, stopWaveformVisualization]);

  const stopRecording = useCallback(() => { stopRecordingFn(); }, [stopRecordingFn]);

  const togglePauseRecording = useCallback(() => {
    if (!recordingState.isRecording || !mediaRecorderRef.current) return;
    if (typeof mediaRecorderRef.current.pause !== 'function') { alert('Pause/resume is not supported in this browser'); return; }
    if (isPaused) {
      if (mediaRecorderRef.current.state === 'paused') {
        try {
          mediaRecorderRef.current.resume();
          setIsPaused(false); isPausedRef.current = false;
          if (streamRef.current && streamRef.current.active) startWaveformVisualization(streamRef.current);
        } catch (err) { alert('Failed to resume recording'); }
      }
    } else {
      if (mediaRecorderRef.current.state === 'recording') {
        try {
          mediaRecorderRef.current.pause();
          setIsPaused(true); isPausedRef.current = true;
          stopTimer(); stopWaveformVisualization();
        } catch (err) { alert('Failed to pause recording'); }
      }
    }
  }, [recordingState.isRecording, isPaused, startWaveformVisualization, stopTimer, stopWaveformVisualization]);

  const discardRecording = useCallback(() => {
    if (recordingState.isRecording) stopRecording();
    if (autoStopTimeoutRef.current) { clearTimeout(autoStopTimeoutRef.current); autoStopTimeoutRef.current = null; }
    setSessionRecordings([null, null, null, null, null]);
    setSessionResults([null, null, null, null, null]);
    setCurrentRecordingIndex(0); setProcessingIndex(-1); setSessionProcessing(false);
    setRecordingState({ isRecording: false, timer: 0, waveformData: new Uint8Array(64), chunkProgress: 0, micPermission: false, audioChunks: [], error: null });
    audioChunksRef.current = []; mediaRecorderRef.current = null; recordingWasAutoStartedRef.current = false;
    stopTimer(); stopWaveformVisualization();
  }, [recordingState.isRecording, stopRecording, stopTimer, stopWaveformVisualization]);

  const getEmployeesForDepartment = (deptName) => (employees || []).filter(emp => emp.department === deptName);

  const processSession = useCallback(async () => {
    if (isContinuousMode) {
      const status = getRecordingStatus(0);
      if (status !== 'done') {
        if (status === 'invalid') alert('Recording has no human voice detected.\n\nPlease re-record. Ensure the person is speaking clearly with minimal background noise.');
        else if (status === 'pending') alert('Recording is still being processed. Please wait.');
        else alert('Please record and analyze the audio before submitting.');
        return;
      }
    } else {
      const allDone = [0, 1, 2, 3, 4].every(i => getRecordingStatus(i) === 'done');
      if (!allDone) {
        const invalidIndices = [0, 1, 2, 3, 4].filter(i => getRecordingStatus(i) === 'invalid');
        if (invalidIndices.length > 0) { alert(`Some recordings have no human voice detected (Recordings: ${invalidIndices.map(i => i + 1).join(', ')}).\n\nPlease re-record those clips before submitting.`); return; }
        const pendingIndices = [0, 1, 2, 3, 4].filter(i => { const status = getRecordingStatus(i); return status === 'pending' || status === 'empty'; });
        if (pendingIndices.length > 0) { alert(`Please ensure all 5 recordings are recorded AND analyzed before submitting.\n\nMissing or pending: Recordings ${pendingIndices.map(i => i + 1).join(', ')}`); return; }
        alert('Please ensure all 5 recordings are recorded AND analyzed before submitting.');
        return;
      }
    }

    if (!selectedSessionStaff) { alert('No staff selected'); return; }
    setSessionProcessing(true);
    try {
      const staffId = selectedSessionStaff.staff_id || selectedSessionStaff.id;
      const sessionDate = new Date().toISOString().split('T')[0];
      const audioUrls = isContinuousMode ? ['recording_1.webm'] : sessionRecordings.map((_, i) => `recording_${i + 1}.webm`);
      const duration = isContinuousMode
        ? `00:${Math.floor(recordingState.timer / 60).toString().padStart(2, '0')}:${(recordingState.timer % 60).toString().padStart(2, '0')}`
        : '00:08:00';

      const sessionRes = await corporateCreateSession({ staff_id: staffId, session_date: sessionDate, duration, audio_urls: audioUrls });
      if (sessionRes.status_code !== 200) throw new Error('Failed to create session');
      const sessionId = sessionRes.data.session_id;

      let allResults = [...sessionResults];
      if (allResults.length !== 5) { while (allResults.length < 5) allResults.push(null); allResults = allResults.slice(0, 5); }

      for (let i = 0; i < sessionRecordings.length; i++) {
        if (!allResults[i] && sessionRecordings[i]) {
          try {
            const convRes = await convertToWav(sessionRecordings[i]);
            const wavBase64 = convRes?.data?.wav_base64;
            if (wavBase64) {
              const wavBytes = atob(wavBase64); const buf = new Uint8Array(wavBytes.length);
              for (let j = 0; j < wavBytes.length; j++) buf[j] = wavBytes.charCodeAt(j);
              const wavBlob = new Blob([buf], { type: 'audio/wav' });
              const predRes = await predictWithWavBlob(wavBlob);
              const apiRes = predRes?.data;
              allResults[i] = apiRes?.RES || apiRes;
            }
          } catch (err) {}
        }
      }

      const validResults = allResults.filter(r => r != null && !r._noVoice && r.top_5);
      if (validResults.length === 0) throw new Error('No valid prediction results available');

      const perClipMetrics = validResults.map(result => mapApiResultToMetrics(result));
      const anyNegativeOnly = perClipMetrics.some(m => m.isNegativeOnlyClip);
      const negativeOnlyRatio = perClipMetrics.filter(m => m.isNegativeOnlyClip).length / perClipMetrics.length;

      const aggregatedScores = {
        emotion_score: parseFloat((perClipMetrics.reduce((sum, m) => sum + (m.emotion_score || 0), 0) / perClipMetrics.length).toFixed(2)),
        motivation_score: parseFloat((perClipMetrics.reduce((sum, m) => sum + (m.motivation_score || 0), 0) / perClipMetrics.length).toFixed(2)),
        burnout_score: parseFloat((perClipMetrics.reduce((sum, m) => sum + (m.burnout_score || 0), 0) / perClipMetrics.length).toFixed(2)),
        engagement_score: parseFloat((perClipMetrics.reduce((sum, m) => sum + (m.engagement_score || 0), 0) / perClipMetrics.length).toFixed(2)),
        stress_score: parseFloat((perClipMetrics.reduce((sum, m) => sum + (m.stress_score || 0), 0) / perClipMetrics.length).toFixed(2)),
        wellbeing_score: parseFloat((perClipMetrics.reduce((sum, m) => sum + (m.wellbeing_score || 0), 0) / perClipMetrics.length).toFixed(2))
      };

      const hasValidEmotionData = aggregatedScores.emotion_score !== null && aggregatedScores.emotion_score !== undefined;
      if (!hasValidEmotionData) { alert('Error: No valid emotion data calculated. Session may have failed to process correctly.'); return; }

      const validatedScores = {};
      Object.keys(aggregatedScores).forEach(key => {
        validatedScores[key] = parseFloat(Math.max(0, Math.min(100, parseFloat(aggregatedScores[key]) || 0)).toFixed(2));
      });

      const emotionDataPayload = allResults.length > 0 ? {
        recordings: allResults, count: allResults.length, timestamp: new Date().toISOString(),
        flags: { negative_only_mode: anyNegativeOnly, negative_only_ratio: parseFloat(negativeOnlyRatio.toFixed(2)) }
      } : { recordings: allResults, count: allResults.length, timestamp: new Date().toISOString(),
        flags: { negative_only_mode: anyNegativeOnly, negative_only_ratio: parseFloat(negativeOnlyRatio.toFixed(2)) }
      };

      await corporateSaveEmotionDetails({
        staff_id: staffId, session_id: sessionId,
        emotion_score: validatedScores.emotion_score, motivation_score: validatedScores.motivation_score,
        burnout_score: validatedScores.burnout_score, engagement_score: validatedScores.engagement_score,
        stress_score: validatedScores.stress_score, wellbeing_score: validatedScores.wellbeing_score,
        emotion_data: emotionDataPayload
      });

      alert('Session completed and saved successfully!');
      onComplete();
    } catch (err) {
      const errorMessage = err.response?.data?.detail || err.response?.data?.message || err.message || 'Unknown error';
      alert('Error processing session: ' + errorMessage);
    } finally {
      setSessionProcessing(false);
    }
  }, [isContinuousMode, selectedSessionStaff, sessionRecordings, sessionResults, recordingState.timer, getRecordingStatus, onComplete]);

  const handleClose = () => {
    if (sessionProcessing) return;
    if (recordingState.isRecording) stopRecording();
    discardRecording();
    onClose();
  };

  return (
    <div className="modal-overlay session-modal-overlay" onClick={handleClose}>
      <div className="session-modal" onClick={e => e.stopPropagation()}>
        <div className="session-modal-header">
          <h2>
            {sessionStep === 1 && t('Select Department')}
            {sessionStep === 2 && t('Select Staff Member')}
            {sessionStep === 3 && `${t('Conduct Session')} - ${selectedSessionStaff?.name || ''}`}
          </h2>
          <button className="modal-close" onClick={handleClose} disabled={sessionProcessing}>×</button>
        </div>

        <div className="session-step-indicator">
          <div className={`step-item ${sessionStep >= 1 ? 'active' : ''} ${sessionStep > 1 ? 'completed' : ''}`}>
            <div className="step-number">1</div>
            <div className="step-label">{t('Department')}</div>
          </div>
          <div className="step-connector"></div>
          <div className={`step-item ${sessionStep >= 2 ? 'active' : ''} ${sessionStep > 2 ? 'completed' : ''}`}>
            <div className="step-number">2</div>
            <div className="step-label">{t('Staff')}</div>
          </div>
          <div className="step-connector"></div>
          <div className={`step-item ${sessionStep >= 3 ? 'active' : ''}`}>
            <div className="step-number">3</div>
            <div className="step-label">{t('Session')}</div>
          </div>
        </div>

        <div className="session-modal-content">
          {sessionStep === 1 && (
            <div className="session-step-content">
              <p className="step-description">{t('Choose a department to view available staff members')}</p>
              <div className="session-search-container">
                <input type="text" className="session-search-input" placeholder={`🔍 ${t('Search departments...')}`} value={departmentSearchQuery} onChange={(e) => setDepartmentSearchQuery(e.target.value)} />
              </div>
              <div className="department-selection-grid">
                {(() => {
                  const filteredDepts = (departments || []).filter(dept => !departmentSearchQuery.trim() ? true : dept.name.toLowerCase().includes(departmentSearchQuery.toLowerCase().trim()));
                  return filteredDepts.length > 0 ? (
                    filteredDepts.map(dept => (
                      <div key={dept.id} className={`department-card-select ${selectedSessionDepartment?.id === dept.id ? 'selected' : ''}`}
                        onClick={() => { setSelectedSessionDepartment(dept); setStaffSearchQuery(''); setTimeout(() => setSessionStep(2), 300); }}>
                        <div className="dept-card-icon">🏢</div>
                        <div className="dept-card-name">{dept.name}</div>
                        <div className="dept-card-count">{getEmployeesForDepartment(dept.name).length} staff</div>
                      </div>
                    ))
                  ) : (
                    <p className="no-data-message">{departmentSearchQuery.trim() ? `No departments found matching "${departmentSearchQuery}"` : 'No departments available. Please add a department first.'}</p>
                  );
                })()}
              </div>
            </div>
          )}

          {sessionStep === 2 && selectedSessionDepartment && (
            <div className="session-step-content">
              <p className="step-description">{t('Select a staff member from')} {selectedSessionDepartment.name}</p>
              <div className="session-search-container">
                <input type="text" className="session-search-input" placeholder={`🔍 ${t('Search staff members...')}`} value={staffSearchQuery} onChange={(e) => setStaffSearchQuery(e.target.value)} />
              </div>
              <div className="staff-selection-grid">
                {(() => {
                  const deptStaff = getEmployeesForDepartment(selectedSessionDepartment.name);
                  const filteredStaff = deptStaff.filter(emp => !staffSearchQuery.trim() ? true : emp.name.toLowerCase().includes(staffSearchQuery.toLowerCase().trim()) || (emp.email && emp.email.toLowerCase().includes(staffSearchQuery.toLowerCase().trim())));
                  return filteredStaff.length > 0 ? (
                    filteredStaff.map(emp => (
                      <div key={emp.id} className={`staff-card-select ${selectedSessionStaff?.id === emp.id ? 'selected' : ''}`}
                        onClick={() => { setSelectedSessionStaff(emp); setTimeout(() => setSessionStep(3), 300); }}>
                        <div className="staff-card-avatar">{emp.name.charAt(0).toUpperCase()}</div>
                        <div className="staff-card-name">{emp.name}</div>
                        <div className="staff-card-email">{t('No email')}</div>
                        <div className="staff-card-metrics">
                          {t('Emotion:')} {emp.emotionScore !== null && emp.emotionScore !== undefined ? emp.emotionScore : 'N/A'}
                          <span>{t('Motivation:')} {emp.motivationScore !== null && emp.motivationScore !== undefined ? emp.motivationScore : 'N/A'}</span>
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="no-data-message">{staffSearchQuery.trim() ? `No staff members found matching "${staffSearchQuery}"` : 'No staff members in this department.'}</p>
                  );
                })()}
              </div>
              <button className="back-button" onClick={() => { setSessionStep(1); setSelectedSessionStaff(null); setStaffSearchQuery(''); }}>{t('← Back to Departments')}</button>
            </div>
          )}

          {sessionStep === 3 && selectedSessionStaff && (
            <div className="session-step-content">
              <p className="step-description">
                {isContinuousMode
                  ? 'Record continuous audio for exactly 40 seconds. The recording will be automatically split into 5 chunks of 8 seconds each for analysis.'
                  : 'Record 5 audio segments, each exactly 8 seconds. After each segment completes, the next one will start automatically.'}
              </p>

              <label className="recording-mode-toggle-label">
                <input type="checkbox" checked={isContinuousMode}
                  onChange={(e) => {
                    if (recordingState.isRecording) { alert('Please stop recording before changing mode'); return; }
                    setIsContinuousMode(e.target.checked);
                    setSessionRecordings([null, null, null, null, null]);
                    setSessionResults([null, null, null, null, null]);
                    setCurrentRecordingIndex(0);
                    setRecordingState(prev => ({ ...prev, timer: 0 }));
                  }}
                  disabled={recordingState.isRecording || sessionProcessing} />
                <span className="toggle-label-text">{t('Continuous Recording')}</span>
              </label>

              <div className="recording-control">
                <button id="recordBtn"
                  className={`record-btn ${recordingState.isRecording ? (isPaused ? 'paused' : 'recording') : (() => {
                    const status = isContinuousMode ? getRecordingStatus(0) : getRecordingStatus(currentRecordingIndex);
                    return status === 'done' ? 'complete' : status === 'invalid' ? 'error' : processingIndex !== -1 ? 'processing' : 'idle';
                  })()}`}
                  onClick={() => {
                    if (recordingState.isRecording) { stopRecording(); } else {
                      const status = isContinuousMode ? getRecordingStatus(0) : getRecordingStatus(currentRecordingIndex);
                      recordingWasAutoStartedRef.current = false;
                      if (status === 'invalid') { startRecording(currentRecordingIndex); }
                      else if (processingIndex === -1) {
                        const allDone = isContinuousMode ? getRecordingStatus(0) === 'done' : [0, 1, 2, 3, 4].every(i => getRecordingStatus(i) === 'done');
                        if (!allDone) {
                          const firstSlot = isContinuousMode ? 0 : findFirstSlotToRecord();
                          if (firstSlot !== -1) { if (!isContinuousMode) setCurrentRecordingIndex(firstSlot); startRecording(isContinuousMode ? null : firstSlot); }
                        }
                      }
                    }
                  }}
                  disabled={sessionProcessing || processingIndex !== -1}
                  aria-label={recordingState.isRecording ? `Recording ${formatTimer(recordingState.timer)} of ${isContinuousMode ? '40' : '8'} seconds` : 'Start recording'}
                  aria-live="polite" role="button">
                  {(() => {
                    if (processingIndex !== -1) { const n = isContinuousMode ? recordingState.chunkProgress : processingIndex + 1; return `Processing ${n}/5...`; }
                    if (recordingState.isRecording) { return isPaused ? `Paused ${isContinuousMode ? formatTimer(recordingState.timer) : `${currentRecordingIndex + 1}/5`}` : `Recording ${isContinuousMode ? formatTimer(recordingState.timer) : `${currentRecordingIndex + 1}/5`}`; }
                    if (isContinuousMode) { if (getRecordingStatus(0) === 'done') return `${t('Complete')} ✓`; } else { if ([0, 1, 2, 3, 4].every(i => getRecordingStatus(i) === 'done')) return `${t('Complete')} ✓`; }
                    const status = isContinuousMode ? getRecordingStatus(0) : getRecordingStatus(currentRecordingIndex);
                    if (status === 'invalid') return `⚠️ ${t('Re-record')}`;
                    if (!isContinuousMode) { const doneCount = getCompletedCount(); if (doneCount > 0 && doneCount < 5) return `${doneCount} ${t('of 5 recorded — Click to continue')}`; }
                    return t('Start Recording');
                  })()}
                </button>

                {recordingState.isRecording && (
                  <div className="waveform-with-timer">
                    <div className="waveform" aria-label="Live audio waveform">
                      {recordingState.waveformData && recordingState.waveformData.length > 0 ? (
                        Array.from(recordingState.waveformData.slice(0, 30)).map((value, idx) => {
                          const normalizedValue = Math.max((value / 255) * 100, 8);
                          return <div key={idx} className="waveform-bar" style={{ height: `${normalizedValue}%`, minHeight: '8px', backgroundColor: isPaused ? '#9ca3af' : '#3b82f6', transition: isPaused ? 'none' : 'height 0.05s linear', animation: isPaused ? 'none' : undefined }} />;
                        })
                      ) : (
                        Array.from({ length: 30 }).map((_, idx) => <div key={idx} className="waveform-bar" style={{ height: '8px', backgroundColor: '#e5e7eb' }} />)
                      )}
                    </div>
                    {!isContinuousMode && (
                      <span className="waveform-timer" aria-live="polite">{formatTimer(recordingState.timer)} / 0:08</span>
                    )}
                  </div>
                )}
              </div>

              <div className="recording-extra-buttons">
                {recordingState.isRecording && mediaRecorderRef.current && typeof mediaRecorderRef.current.pause === 'function' && (
                  <button className="pause-resume-btn" onClick={togglePauseRecording} aria-label={isPaused ? 'Resume recording' : 'Pause recording'}>
                    {isPaused ? '▶ Resume' : '|| Pause'}
                  </button>
                )}
                {(recordingState.isRecording || (isContinuousMode ? getRecordingStatus(0) !== 'empty' : [0, 1, 2, 3, 4].some(i => getRecordingStatus(i) !== 'empty'))) && (
                  <button className="discard-btn" onClick={discardRecording} disabled={sessionProcessing || processingIndex !== -1} aria-label="Discard and restart recording">
                    {t('Discard & Restart')}
                  </button>
                )}
                {recordingState.error && (
                  <div className="recording-error-message" role="alert">⚠️ {recordingState.error}</div>
                )}
              </div>

              <div className="session-modal-actions">
                {(() => {
                  const checkSubmissionReady = () => {
                    if (sessionProcessing) return { ready: false, reason: 'Session is processing' };
                    if (recordingState.isRecording) return { ready: false, reason: 'Currently recording' };
                    if (processingIndex !== -1) return { ready: false, reason: `Processing recording ${processingIndex + 1}` };
                    if (isContinuousMode) {
                      if (!sessionRecordings[0]) return { ready: false, reason: 'Recording is missing' };
                      const result = sessionResults[0];
                      if (!result) return { ready: false, reason: 'Result is missing' };
                      if (result._noVoice) return { ready: false, reason: 'Recording has no voice flag' };
                      const hasTop5 = (typeof result.top_5 === 'string' && result.top_5.length > 0) || (typeof result.top_5 === 'object' && result.top_5 !== null) || (result.emotion && result.motivation);
                      if (!hasTop5) return { ready: false, reason: 'Result missing top_5 data' };
                    } else {
                      if (sessionRecordings.length < 5) return { ready: false, reason: `Not enough recordings: ${sessionRecordings.length}/5` };
                      for (let i = 0; i < 5; i++) {
                        const result = sessionResults[i];
                        const recording = sessionRecordings[i];
                        if (result && result._noVoice) return { ready: false, reason: `Recording ${i + 1} has no voice` };
                        if (recording && recording._noVoice) return { ready: false, reason: `Recording ${i + 1} has no voice` };
                      }
                      for (let i = 0; i < 5; i++) {
                        if (!sessionRecordings[i]) return { ready: false, reason: `Recording ${i + 1} is missing` };
                        const result = sessionResults[i];
                        if (!result) return { ready: false, reason: `Result ${i + 1} is missing` };
                        if (result._noVoice) return { ready: false, reason: `Result ${i + 1} has no voice flag` };
                        const hasTop5 = (typeof result.top_5 === 'string' && result.top_5.length > 0) || (typeof result.top_5 === 'object' && result.top_5 !== null) || (result.emotion && result.motivation);
                        if (!hasTop5) return { ready: false, reason: `Result ${i + 1} missing top_5 data` };
                      }
                    }
                    return { ready: true, reason: 'All checks passed' };
                  };
                  const submissionStatus = checkSubmissionReady();
                  return (
                    <button className="submit-session-button" onClick={processSession} disabled={!submissionStatus.ready} title={!submissionStatus.ready ? submissionStatus.reason : 'Ready to submit'}>
                      {sessionProcessing ? `⏳ ${t('Processing...')}` : `✅ ${t('Submit Session')}`}
                    </button>
                  );
                })()}
                <button className="back-button" onClick={() => { setSessionStep(2); setSessionRecordings([null, null, null, null, null]); setSessionResults([null, null, null, null, null]); setCurrentRecordingIndex(0); }} disabled={sessionProcessing}>
                  {t('← Back to Staff')}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
